<script setup lang="ts">
import {
  Currency,
  Customer,
  Item,
  ItemPackingPrice,
  TaxDefinition,
  Vendor,
  WarehouseShelf,
} from "../../../types/models";
import { isEmpty, formatNumber } from "../../../utils/CommonFunctions";
import { nextTick, onMounted, ref, watch } from "vue";
import { api } from "../../../api/Api";
import Toast from "../../../services/Toast";
import SearchSelect from "../form/SearchSelect.vue";
import IconComponent from "../common/IconComponent.vue";

const props = defineProps<{
  items?: Item[] | [];
  readonly?: boolean;
  warehouses_id?: number | string | null | undefined;
  price_lists_id?: number | string;
  currency?: Currency
  exchange_rate: number
  customer?: Customer | "";
  purchase?: boolean
  vendor?: Vendor | "";
  allowNewRow: boolean;
  allowChangePrice?: boolean;
  allowDiscount?: boolean;
  ignoreBatchSerial?: boolean;
  shelfManaged?: boolean;
  shelfs?: WarehouseShelf[] | [];
  taxDetails: TaxDefinition[];
}>();

const emit = defineEmits<{
  (e: "save"): void;
  (e: "showItemGrid"): void;
  (e: "addItem", item?: Item): void;
}>();

const code = ref("");
const selectItems = ref<Item[] | []>([]);
const showItemsDialog = ref(false);
const item = ref<Item>();
const showPackingPriceDropDown = ref(false);
const itemList = ref<Item[]>([]);

//const taxDetails = ref([]);

const handleTax = (item: Item) => {
  const tax: TaxDefinition = props.taxDetails?.find(
    (e: TaxDefinition) => e.id == item.tax_details_id
  ) || { id: "", name: "", percentage: 0 };
  item.tax_rate = tax ? tax.percentage : 0;
  updateLineTotal(item);
};

function handleQuantityEnter() {
  /*item.price = item.original_price
  if ((item.batches?.length ?? 0) == 1 && !props.ignoreBatchSerial) {
    if (item.tracked_by == 'Batch' && item.batches && item.batches.length > 0) {
      item.batches[0].quantity = item.inventory_qty ?? 0;
    }
  }*/
  if (props.allowChangePrice) {
    nextTick(() => {
      const input = document.getElementById("price_mobile");
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  } else {
    handleAddItem();
  }
}

const handleAddItem = () => {
  emit("addItem", item.value);
  clearItem();
  nextTick(() => {
    const input = document.getElementById("code_mobile");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

function handleUnitPriceEnter() {}
const updatePackingPrice = (packing: ItemPackingPrice) => {
  if (item.value) {
    item.value.sales_uom_id = packing.unit_of_measures_id;
    item.value.sales_uom = packing.unit_of_measure;
    item.value.sales_uom_qty = packing.inventory_qty;
    item.value.price = packing.selling_price;
    showPackingPriceDropDown.value = false;
    updateLineTotal(item.value);
  }
};

const clearPackingPrice = (item: Item) => {
  item.sales_uom_id = item.default_sales_uom_id;
  item.sales_uom = item.unit_of_measure;
  item.sales_uom_qty = item.default_sales_uom_qty;
  item.price = item.original_price;
  showPackingPriceDropDown.value = false;
  updateLineTotal(item);
};

function updateLineTotal(item: Item) {
  item.inventory_qty = (item.quantity || 1) * (item.sales_uom_qty || 1);
  item.sub_total = (item.quantity || 1) * (item.price || 0);
  item.discount_a = (item.sub_total * item.discount_p || 0) / 100;
  item.value_before_tax = item.sub_total - item.discount_a;
  item.tax_amount =
    ((item.sub_total - item.discount_a) * (item.tax_rate || 0)) / 100;
  item.net_total = item.sub_total - item.discount_a + item.tax_amount;

  /*let rounding = tokenManager.getSetting('sales_documents_rounding')
  if (rounding === 'Round Up to 0 decimals' && item.net_total > 0) {
    item.net_total = Math.ceil(item.net_total)
    item.price = item.net_total/item.quantity
  } else if (rounding == 'Round Down to 0 decimals' && item.net_total > 0) {
    item.net_total = Math.floor(item.net_total)
    item.price = item.net_total/item.quantity
  } else if (rounding == 'Round to 0 decimals' && item.net_total > 0) {
    item.net_total = Math.round(item.net_total)
    item.price = item.net_total/item.quantity
  }*/

  //emit('items', itemList.value)
}

function updateDiscount(item: Item) {
  item.discount_p = ((item.discount_a ?? 0) / (item.sub_total || 1)) * 100;
  updateLineTotal(item);
}

async function handleItemCodeEnter() {
  if (isEmpty(code.value)) return;
  const params = {
    search_query: code.value,
    price_lists_id: props.price_lists_id,
    warehouses_id: props.warehouses_id,
  };
  const response = await api.post(
    "inventory/item-management/items/search",
    params
  );
  if (response.success == true) {
    if (response.data.length == 1) {
      setItem(response.data[0]);
    } else if (response.data.length > 1) {
      selectItems.value = response.data;
      showItemsDialog.value = true;
    } else {
      Toast.show("Item not found", "warning");
    }
  } else {
    Toast.show("Item not found", "warning");
  }
}

const clearItem = () => {
  item.value = {
    id: itemList.value.length,
    items_id: "",
    photo: "",
    code: "",
    name: "",
    item_name: "",
    flexi_price: false,
    ignore_discount: false,
    sales_name: "",
    item_types_id: "",
    tracked_by: "Quantity",
    purchase_uom: "",
    purchase_uom_qty: 1,
    sales_uom: "",
    sales_uom_qty: 1,
    is_package: false,
    use_package_price: false,
    is_expiry: false,
    tax_details_id: "",
    inventory_uom_id: "",
    purchase_uom_id: "",
    sales_uom_id: "",
    has_variants: false,
    variants: "",
    tax: "",
    tax_rate: 0,
    purchase_order_items_id: "",
    purchase_goods_receipts_id: "",
    warehouses_id: props.warehouses_id ?? null,
    default_sales_uom_id: "",
    default_sales_uom_qty: 1,
    inventory_uom_qty: 0,
    item_variants_id: "",
    unit_of_measures_id: "",
    unit_of_measure: "",
    on_hand: 0,
    quantity: 1,
    uom_qty: 1,
    inventory_qty: 1,
    cost: 0,
    original_price: 0,
    price: 0,
    selling_price: 0,
    sub_total: 0,
    discount_p: 0,
    discount_a: 0,
    value_before_tax: 0,
    tax_amount: 0,
    net_total: 0,
    remarks: "",
    uid: "",
    batches: [],
    serials: [],
    packing_prices: [],
    quantity_prices: [],
    shelfs: [],
  };
  const shelfs: WarehouseShelf[] = [];
  props.shelfs?.forEach((shelf) => {
    const s: WarehouseShelf = {
      id: shelf.id,
      sales_invoice_items_id: "",
      warehouses_id: props.warehouses_id ?? null,
      warehouse_shelfs_id: shelf.id,
      on_hand: 0,
      items_id: "",
      item_variants_id: "",
      quantity: 0,
      code: shelf.code,
      name: shelf.name,
      status_id: "",
    };
    shelfs.push(s);
  });
  if (item.value) {
    item.value.shelfs = shelfs;
  }
};

const setItem = (result: any) => {
  item.value = {
    id: result.id,
    items_id: result.id,
    photo: result.photo,
    code: result.code,
    flexi_price: result.flexi_price,
    ignore_discount: result.ignore_discount,
    name: result.name,
    item: result.item,
    item_name: result.name || result.item,
    tracked_by: result.tracked_by,
    sales_name: result.sales_name,
    item_types_id: result.item_types_id,
    purchase_uom: result.purchase_uom,
    purchase_uom_qty: result.purchase_uom_qty,
    inventory_uom_qty: result.inventory_uom_qty,
    sales_uom: result.sales_uom,
    sales_uom_qty: result.sales_uom_qty,
    default_sales_uom_id: result.sales_uom_id,
    default_sales_uom_qty: result.sales_uom_qty,
    is_package: result.is_package,
    use_package_price: result.use_package_price,
    is_expiry: result.is_expiry,
    tax_details_id: result.tax_details_id,
    inventory_uom_id: result.inventory_uom_id,
    purchase_uom_id: result.purchase_uom_id,
    sales_uom_id: result.sales_uom_id,
    has_variants: result.has_variants,
    variants: result.variants,
    tax: result.tax,
    tax_rate: result.tax_rate,
    uid: result.uid,
    item_variants_id: result.item_variants_id,
    unit_of_measures_id: result.unit_of_measures_id,
    unit_of_measure: result.unit_of_measure,
    on_hand: result.on_hand,
    quantity: 1,
    uom_qty: result.sales_uom_qty,
    inventory_qty: 1,
    cost: result.cost,
    original_price: result.selling_price,
    price: result.selling_price,
    selling_price: result.selling_price,
    sub_total: result.selling_price,
    discount_p: 0,
    discount_a: 0,
    value_before_tax: result.selling_price,
    tax_amount: (result.selling_price * result.tax_rate) / 100,
    net_total:
      result.selling_price + (result.selling_price * result.tax_rate) / 100,
    remarks: "",
    batches: result.batches,
    serials: result.serials,
    packing_prices: result.packing_prices,
    quantity_prices: result.quantity_prices,
    warehouses_id: props.warehouses_id ?? null,
  };
  nextTick(() => {
    if (item.value) {
      item.value.tax_details_id = result.tax_details_id;
    }
  });

  nextTick(() => {
    const input = document.getElementById("quantity_mobile");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

watch(
  () => props.allowNewRow,
  (newValue) => {
    if (newValue == true) {
      nextTick(() => {
        const input = document.getElementById("code_mobile");
        if (input) {
          input.focus();
          (input as HTMLInputElement).select();
        }
      });
    }
  },
  { immediate: true }
);

onMounted(() => {
  itemList.value = props.items ?? [];
});
</script>

<template>
  <div>
    <div class="relative" v-if="!readonly && (customer || vendor)">
      <input
        :id="'code_mobile'"
        v-model="code"
        type="text"
        class="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
        @keyup.enter="handleItemCodeEnter()"
      />
      <button
        @click="handleItemCodeEnter"
        class="absolute right-10 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        title="Search items"
      >
        <IconComponent name="Search" />
      </button>
      <button
        @click="$emit('showItemGrid')"
        class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        title="Search items"
      >
        <IconComponent name="LayoutGrid" />
      </button>
    </div>
    <span v-else-if="!readonly && !purchase">Select Customer</span>
    <span v-else-if="!readonly && purchase">Select Vendor</span>
    <template v-if="item && item.name">
      <table>
        <tbody>
          <tr>
            <td class="px-4 py-2" colspan="3">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Name
              </span>
              {{ item.name ?? item.item }}
            </td>
          </tr>
          <tr>
            <td class="px-4 py-2 relative group">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Packing
              </span>
              <span
                >{{ item.sales_uom }} x
                {{ formatNumber(item.sales_uom_qty, 2, 0) }} pcs</span
              >
              <button
                v-if="item.packing_prices && item.packing_prices.length > 0 && !readonly"
                @click="showPackingPriceDropDown = !showPackingPriceDropDown"
                class="absolute bottom-1 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                title="Edit item"
              >
                ✏️
              </button>
              <div
                v-if="showPackingPriceDropDown"
                class="absolute mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[1000]"
              >
                <button
                  v-for="pack in item.packing_prices?.filter(
                    (e) => e.price_lists_id == props.price_lists_id
                  )"
                  @click="updatePackingPrice(pack)"
                  class="block w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {{ pack.unit_of_measure }} @
                  {{ formatNumber(pack.selling_price) }}
                </button>
              </div>
              <button
                v-if="!readonly && item.sales_uom_id != item.default_sales_uom_id"
                @click="clearPackingPrice(item)"
                class="absolute bottom-1 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                title="Clear"
              >
                🗑️
              </button>
              <!--<button 
                  v-if="packingEditRowIndex == index && !readonly"
                    @click="packingEditRowIndex = -1"
                    class="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    title="Cancel item"
                  >
                  ✕
                  </button>-->
            </td>
            <td class="px-4 py-2 text-right">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Price
              </span>
              <input
                v-if="!props.readonly && props.allowChangePrice"
                :id="'price_mobile'"
                v-model.number="item.price"
                type="number"
                min="1"
                class="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @keyup.enter="handleUnitPriceEnter"
              />
              <span v-else>{{ formatNumber(item.price) }}</span>
            </td>
            <td class="px-4 py-2 col-span-2 text-right">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Subtotal
              </span>
              {{ formatNumber(item.sub_total) }}
            </td>
          </tr>
          <tr>
            <td class="px-4 py-2">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                On hand
              </span>
              {{ item.on_hand }}
            </td>
            <td class="px-4 py-2 text-right" colspan="2">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Quantity
              </span>
              <input
                v-if="!props.readonly"
                :id="'quantity_mobile'"
                v-model.number="item.quantity"
                type="number"
                min="1"
                class="px-3 py-2 max-w-[120-px] w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @input="updateLineTotal(item)"
                @keyup.enter="handleQuantityEnter"
              />
              <span v-else>{{ item.quantity }}</span>
            </td>
          </tr>
          <tr>
            <td class="px-4 py-2">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Discount %
              </span>
              <input
                v-if="!props.readonly && props.allowDiscount"
                v-model.number="item.discount_p"
                type="number"
                min="0"
                max="100"
                class="px-3 py-2 max-w[120px] min-w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @input="updateLineTotal(item)"
              />
              <span v-else>{{ item.discount_p }}</span>
            </td>
            <td class="px-4 py-2 text-right" colspan="2">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Discount Amount
              </span>
              <input
                v-if="!props.readonly"
                v-model.number="item.discount_a"
                type="number"
                min="0"
                max="100"
                class="px-3 py-2 max-w[120px] min-w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @keyup.enter="updateDiscount(item)"
              />
              <span v-else>{{ formatNumber(item.discount_a ?? 0) }}</span>
            </td>
          </tr>
          <!--<tr>
            <td class="px-4 py-3 col-span-2">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Value Before Tax
              </span>
              {{ formatNumber(item.value_before_tax ?? 0) }}
            </td>
          </tr>-->
          <tr>
            <td class="px-4 py-2 text-left">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Tax Definition
              </span>
              <SearchSelect
                label=""
                v-if="!readonly"
                v-model="item.tax_details_id"
                :options="taxDetails"
                @select="handleTax(item)"
              />
              <span v-else>{{ item.tax }}</span>
            </td>
            <td class="px-4 py-2 relative text-right" colspan="2">
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Tax Amount
              </span>
              <span class="absolute top-1 right-2 text-[10px] text-gray-400">
                {{ item.tax_rate }}%
              </span>
              {{ formatNumber(item.tax_amount ?? 0) }}
            </td>
          </tr>
          <tr>
            <td
              colspan="3"
              class="bg-white dark:bg-gray-900 z-10 px-4 py-3 font-medium col-span-2 text-center"
            >
              <span
                class="block text-[10px] text-gray-500 uppercase tracking-wide"
              >
                Net Total
              </span>
              {{ formatNumber(item.net_total ?? 0) }}
            </td>
          </tr>
          <tr>
            <td
              colspan="3"
              class="px-4 py-2 font-medium col-span-2"
              @click="handleAddItem"
            >
              <button
                class="px-4 py-2 w-full text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Add Item
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>
